import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, RotateCcw, Users, Calendar } from 'lucide-react';
import { ALL_DESTINATIONS, ROUTE_PATHS } from '@/lib/index';

interface FlightSearchProps {
  compact?: boolean;
}

export default function FlightSearch({ compact = false }: FlightSearchProps) {
  const navigate = useNavigate();
  const [tripType, setTripType] = useState<'oneway' | 'roundtrip'>('roundtrip');
  const [fromCity] = useState('广州');
  const [fromCode] = useState('CAN');
  const [toCity, setToCity] = useState('');
  const [toCode, setToCode] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [cabinClass, setCabinClass] = useState<'economy' | 'business'>('economy');
  const [showDest, setShowDest] = useState(false);

  const handleSearch = () => {
    // Build deep link to Air Tanzania official booking system
    const baseUrl = 'https://www.airtanzania.co.tz';
    const params = new URLSearchParams({
      from: fromCode,
      to: toCode || 'DAR',
      depart: departDate,
      return: returnDate,
      passengers: String(passengers),
      cabin: cabinClass,
    });
    window.open(`${baseUrl}/book?${params.toString()}`, '_blank');
    navigate(ROUTE_PATHS.BOOKING);
  };

  const wrapCls = compact
    ? 'bg-white rounded-2xl shadow-xl p-4'
    : 'bg-white rounded-2xl shadow-2xl p-6';

  return (
    <div className={wrapCls}>
      {/* Trip type */}
      <div className="flex gap-2 mb-4">
        {(['roundtrip', 'oneway'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTripType(t)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              tripType === t
                ? 'bg-secondary text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            {t === 'roundtrip' ? '往返' : '单程'}
          </button>
        ))}
        <button
          onClick={() => setCabinClass(cabinClass === 'economy' ? 'business' : 'economy')}
          className={`ml-auto px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            cabinClass === 'business'
              ? 'bg-accent text-accent-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/70'
          }`}
        >
          {cabinClass === 'economy' ? '经济舱' : '✨ 公务舱'}
        </button>
      </div>

      <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
        {/* From */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">出发地</label>
          <div className="flex items-center gap-2 px-3 py-3 bg-muted rounded-xl border border-border">
            <div className="text-secondary">
              <Search size={15} />
            </div>
            <div>
              <div className="font-semibold text-sm text-foreground">{fromCity}</div>
              <div className="text-xs text-muted-foreground font-mono">{fromCode} · 白云国际机场</div>
            </div>
          </div>
        </div>

        {/* To */}
        <div className="space-y-1 relative">
          <label className="text-xs text-muted-foreground font-medium">目的地</label>
          <div
            className="flex items-center gap-2 px-3 py-3 bg-muted rounded-xl border border-border cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setShowDest(!showDest)}
          >
            <ArrowRight size={15} className="text-primary" />
            <div>
              {toCity ? (
                <>
                  <div className="font-semibold text-sm">{toCity}</div>
                  <div className="text-xs text-muted-foreground font-mono">{toCode}</div>
                </>
              ) : (
                <div className="text-muted-foreground text-sm">选择目的地</div>
              )}
            </div>
          </div>
          {showDest && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-xl z-20 max-h-56 overflow-auto">
              {ALL_DESTINATIONS.map((d) => (
                <button
                  key={d.code}
                  className="w-full px-4 py-2.5 text-left text-sm hover:bg-muted transition-colors flex items-center justify-between"
                  onClick={() => {
                    setToCity(d.name);
                    setToCode(d.code);
                    setShowDest(false);
                  }}
                >
                  <span>{d.name} <span className="text-muted-foreground">({d.country})</span></span>
                  <span className="font-mono text-xs text-muted-foreground">{d.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dates */}
        <div className={`space-y-1 ${compact ? '' : 'col-span-1 md:col-span-2 lg:col-span-1'}`}>
          <label className="text-xs text-muted-foreground font-medium">
            出发日期{tripType === 'roundtrip' ? ' / 返程日期' : ''}
          </label>
          <div className="flex gap-2">
            <div className="flex items-center gap-2 px-3 py-3 bg-muted rounded-xl border border-border flex-1">
              <Calendar size={15} className="text-primary shrink-0" />
              <input
                type="date"
                value={departDate}
                onChange={(e) => setDepartDate(e.target.value)}
                className="bg-transparent text-sm w-full outline-none text-foreground"
                placeholder="出发"
              />
            </div>
            {tripType === 'roundtrip' && (
              <div className="flex items-center gap-2 px-3 py-3 bg-muted rounded-xl border border-border flex-1">
                <RotateCcw size={15} className="text-accent shrink-0" />
                <input
                  type="date"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  className="bg-transparent text-sm w-full outline-none text-foreground"
                  placeholder="返程"
                />
              </div>
            )}
          </div>
        </div>

        {/* Passengers + Search */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground font-medium">乘客</label>
          <div className="flex gap-2">
            <div className="flex items-center gap-2 px-3 py-3 bg-muted rounded-xl border border-border flex-1">
              <Users size={15} className="text-primary" />
              <select
                value={passengers}
                onChange={(e) => setPassengers(Number(e.target.value))}
                className="bg-transparent text-sm w-full outline-none text-foreground"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n} 位成人
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={handleSearch}
        className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 bg-secondary hover:bg-secondary/90 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl active:scale-[0.99]"
      >
        <Search size={16} />
        搜索航班
      </button>
    </div>
  );
}
